/**
 * ONE OPEN CONNECTION TO THE SERVER, KEPT OPEN. (Web chat 4/6, #225.)
 *
 * Server-Sent Events read with `fetch` rather than `EventSource`, because the Inbox's side has to
 * carry the colleague's session in a header, and EventSource cannot send one — a session in the
 * address would end up in logs. Her side uses the same code so both are tested once.
 *
 * It reconnects by itself: 1 s, 2 s, 5 s, 10 s, then every 30 s, and back to 1 s once a connection
 * has opened. `onState` says 'open' or 'down', so a box can read more often while it is down —
 * the connection only ever makes things faster; reading every few seconds is what keeps nothing lost.
 * A 404 means the conversation is not hers or not there any more: it stops ('gone').
 *
 * Nothing here reads the clock; the waits are timers, and both are passed in for the tests.
 */
const RETRY_MS = Object.freeze([1000, 2000, 5000, 10_000, 30_000])

/** Splits what has arrived into whole events and what is left over for the next chunk. */
export function parseEvents(buffer) {
  const events = []
  const parts = String(buffer).replace(/\r\n/g, '\n').split('\n\n')
  const rest = parts.pop()
  for (const block of parts) {
    const data = block.split('\n').filter((l) => l.startsWith('data:')).map((l) => l.slice(5).trimStart()).join('\n')
    if (!data) continue /* a comment (": beat") or an empty block */
    try {
      const e = JSON.parse(data)
      if (e && typeof e === 'object' && typeof e.type === 'string') events.push(e)
    } catch { /* not ours; ignored */ }
  }
  return { events, rest }
}

/* The server says something at least every 20 s; nothing for this long means the line died silently. */
const SILENCE_MS = 45_000

export function openLiveStream({ url, fetchImpl = null, headers = async () => ({}), onEvent, onState = () => {},
  setTimer = setTimeout, clearTimer = clearTimeout, retryMs = RETRY_MS, silenceMs = SILENCE_MS }) {
  let stopped = false
  let attempt = 0
  let timer = null
  let ctl = null

  const later = () => {
    if (stopped) return
    const wait = retryMs[Math.min(attempt, retryMs.length - 1)]
    attempt += 1
    timer = setTimer(connect, wait)
  }

  async function connect() {
    timer = null
    if (stopped) return
    ctl = typeof AbortController === 'function' ? new AbortController() : null
    let res
    try {
      res = await (fetchImpl ?? fetch)(url, { headers: { accept: 'text/event-stream', ...(await headers()) }, ...(ctl ? { signal: ctl.signal } : {}) })
    } catch {
      if (!stopped) { onState('down'); later() }
      return
    }
    if (stopped) return
    if (res.status === 404) { stopped = true; onState('gone'); return }
    if (!res.ok || !res.body?.getReader) { onState('down'); later(); return }
    const reader = res.body.getReader()
    const decoder = new TextDecoder()
    let buffer = ''
    /*
      OPEN ONLY ONCE THE SERVER HAS SPOKEN (review of #225): a proxy that answers 200 and hangs up at
      once must not reset the wait to one second and loop. And a line that goes silent — a laptop
      waking up, Wi-Fi handing over to the phone network — is given up after `silenceMs`.
    */
    let opened = false
    let silence = null
    const quiet = () => { if (silence) clearTimer(silence); silence = setTimer(() => { try { ctl?.abort() } catch { /* closed */ } }, silenceMs) }
    quiet()
    try {
      for (;;) {
        const { value, done } = await reader.read()
        if (done || stopped) break
        quiet()
        buffer += decoder.decode(value, { stream: true })
        const { events, rest } = parseEvents(buffer)
        buffer = rest
        for (const e of events) {
          if (!opened) { opened = true; attempt = 0; onState('open') }
          try { onEvent(e) } catch { /* the box's own problem, not the connection's */ }
        }
      }
    } catch { /* dropped */ }
    if (silence) clearTimer(silence)
    if (!stopped) { onState('down'); later() }
  }

  connect()
  return () => {
    stopped = true
    if (timer) clearTimer(timer)
    try { ctl?.abort() } catch { /* already closed */ }
  }
}
